import { Box, Collapse, Typography } from '@mui/material';
import { ChevronDown, ChevronRight, Hash } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilValue } from 'recoil';
import IdField from '~/components/Fields/IdField';
import { LabelText } from '~/components/Fields/LabeledValue';
import SectionLabel from '~/components/SectionLabel';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { showSensitiveInfoState } from '~/state/atoms/viewerAtoms';
import { useIsInObjectDetails } from './objectDetailsContext';

interface IdentityRow {
    /** Pre-resolved label. Callers hold the intl instance already. */
    label: string;
    /** The UID. PHI: masked with copy disabled while sensitive info is hidden. */
    id?: string;
    /**
     * Value for a non-UID identity row. A string renders as plain text
     * (blurred when `sensitive`); any other node is rendered as-is and
     * carries its own masking.
     */
    value?: ReactNode;
    /** PHI value row (e.g. a label): blurred with copy disabled while hidden. */
    sensitive?: boolean;
    /** Small print under the value: what the UID points at. */
    note?: ReactNode;
}

interface IdentitySectionProps {
    rows: ReadonlyArray<IdentityRow>;
}

/**
 * The identity block of a details panel: SOP class, UIDs and the objects
 * this one references. Each UID has a copy control beside it, and stays
 * masked with copy disabled while sensitive info is hidden.
 *
 * In the details dialog the UIDs print in full, so they can be checked
 * against PACS or the TPS, and the section folds. A hover panel gets
 * neither: it has no room for four wrapped UIDs, and it closes on any
 * click, so a fold there could never be opened.
 */
export default function IdentitySection({ rows }: IdentitySectionProps) {
    const isInObjectDetails = useIsInObjectDetails();
    const [folded, setFolded] = useState(false);
    const open = !isInObjectDetails || !folded;
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);

    const shown = rows.filter((row) => row.id || row.value);
    if (shown.length === 0) {
        return null;
    }

    // The copy hint would be a lie while masking disables every copy.
    const hintId = open
        ? showSensitiveInfo
            ? 'properties.identity.copyHint'
            : undefined
        : 'properties.identity.hint';

    const heading = (
        <>
            <Hash size={11} />
            <SectionLabel id="properties.identity" sx={{ color: 'inherit' }} />
            {hintId && (
                <Typography fontSize={10} color="text.disabled" ml={0.5}>
                    <FormattedMessage id={hintId} />
                </Typography>
            )}
        </>
    );

    return (
        <Box mt={1.5} borderTop={1} borderColor="divider" pt={1}>
            {isInObjectDetails ? (
                <Box
                    component="button"
                    type="button"
                    onClick={() => setFolded((f) => !f)}
                    aria-expanded={open}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        width: '100%',
                        p: 0,
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: 'text.secondary',
                        '&:hover': { color: 'text.primary' },
                    }}
                    data-testid="identity-section-toggle"
                >
                    {open ? (
                        <ChevronDown size={12} />
                    ) : (
                        <ChevronRight size={12} />
                    )}
                    {heading}
                </Box>
            ) : (
                <Box
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    color="text.secondary"
                >
                    {heading}
                </Box>
            )}

            {/*
             * unmountOnExit: a collapsed Collapse still renders its
             * children, and an identifier nobody asked for should not sit
             * in the DOM. Masking applies either way.
             */}
            <Collapse in={open} unmountOnExit>
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(auto-fill, minmax(220px, 1fr))"
                    gap="10px 24px"
                    mt={1}
                    data-testid="identity-section-rows"
                >
                    {shown.map((row) => (
                        <Box key={row.label} minWidth={0}>
                            <LabelText sx={{ fontSize: 10 }}>
                                {row.label}
                            </LabelText>
                            {row.id ? (
                                <IdField id={row.id} full={isInObjectDetails} />
                            ) : typeof row.value === 'string' ? (
                                <Typography fontSize={11}>
                                    {row.sensitive ? (
                                        <SensitiveInfoTypography>
                                            {row.value}
                                        </SensitiveInfoTypography>
                                    ) : (
                                        row.value
                                    )}
                                </Typography>
                            ) : (
                                row.value
                            )}
                            {row.note && (
                                <Typography
                                    fontSize={10}
                                    color="text.disabled"
                                    sx={{ wordBreak: 'break-word' }}
                                >
                                    {row.note}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
}
